// Modal de agente do Talk.ai — mockups "Modal Agente Personalidade /
// Conhecimento / Avançado" (900px). Nome e o vínculo (conexão, filas e
// "antes da distribuição") ficam fora das abas porque valem para as três;
// cada aba é um par de colunas, com a da direita mostrando o efeito do que
// se edita à esquerda.
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import FaceIcon from "@material-ui/icons/Face";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import TuneIcon from "@material-ui/icons/Tune";
import MoodIcon from "@material-ui/icons/Mood";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ErrorIcon from "@material-ui/icons/Error";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import ArticleIcon from "@material-ui/icons/Assignment";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import GraphicEqIcon from "@material-ui/icons/GraphicEq";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { toast } from "react-toastify";

import AerosDialog from "../AerosDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import usePromptModalStyles from "./promptModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useTheme, alpha } from "@material-ui/core/styles";
import {
  TONES,
  toneLabel,
  MODELS,
  VOICES,
  voiceShortName,
  filesSummary,
  simulationScript,
  knowledgeCounter,
  KNOWLEDGE_LIMIT,
  formatFileSize,
} from "../../pages/Prompts/promptRules";
import {
  filasDaConexao,
  limpaFilasInvalidas,
  filasForaDaConexao,
  ocupacaoDasFilas,
  antesDaFilaOcupadoPor,
  conexoesComAntesDaFilaExtra,
  filasComIntegracao,
  filasComChatbot,
  conexaoTemIntegracao,
  conexaoTemMenu,
  completaFilasDasConexoes,
  erroDoVinculo,
} from "./vinculoRules";
import { aplicaPersonalidade } from "./personalidadeRules";

const MAX_FILES = 20;

const CAMPOS_PERSONALIDADE = ["agentName", "tone", "useEmojis"];

const INITIAL = {
  name: "",
  prompt: "",
  voice: "texto",
  voiceKey: "",
  voiceRegion: "",
  maxTokens: 100,
  temperature: 1,
  apiKey: "",
  // Vínculo: conexão obrigatória + filas dela e/ou "antes da distribuição".
  whatsappId: "",
  queueIds: [],
  atendeAntesDaFila: false,
  maxMessages: 10,
  agentName: "",
  tone: "amigavel",
  useEmojis: true,
  restrictions: "",
  transferInstructions: "",
  transferMessage: "",
  knowledgeText: "",
  model: "gpt-4.1-mini",
};

const fileVisual = (name = "") => {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return { Icon: PictureAsPdfIcon, color: AEROS.error };
  if (lower.endsWith(".docx") || lower.endsWith(".doc"))
    return { Icon: ArticleIcon, color: "#C08BFF" };
  return { Icon: DescriptionIcon, color: AEROS.cyan };
};

const PromptModal = ({ open, onClose, promptId, initialTab = 0 }) => {
  const classes = usePromptModalStyles();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);
  const { whatsApps = [] } = useContext(WhatsAppsContext) || {};

  const [prompt, setPrompt] = useState(INITIAL);
  const [queues, setQueues] = useState([]);
  // Lista enxuta de todos os agentes (GET /prompt?all=true): quem já ocupa
  // cada (conexão, fila) e a vaga de "antes da distribuição".
  const [agentes, setAgentes] = useState([]);
  // Filas gravadas no agente, para nomear as que saíram da conexão.
  const [filasSalvas, setFilasSalvas] = useState([]);
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Nome, tom e emojis não viram frase no servidor (1.13.0): a troca reescreve
  // as linhas de personalidade no topo do prompt base, visíveis e editáveis.
  const change = (campo, valor) =>
    setPrompt((atual) => {
      const novo = { ...atual, [campo]: valor };
      if (CAMPOS_PERSONALIDADE.includes(campo)) {
        novo.prompt = aplicaPersonalidade(atual.prompt, novo);
      }
      return novo;
    });

  useEffect(() => {
    if (open) setTab(initialTab || 0);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    async function loadQueues() {
      try {
        const [{ data }, { data: todos }] = await Promise.all([
          api.get("/queue"),
          api.get("/prompt", { params: { all: true } }),
        ]);
        setQueues(Array.isArray(data) ? data : []);
        setAgentes(Array.isArray(todos?.prompts) ? todos.prompts : []);
      } catch (err) {
        toastError(err);
      }
    }
    loadQueues();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    async function loadPrompt() {
      if (!promptId) {
        setPrompt({ ...INITIAL, prompt: aplicaPersonalidade("", INITIAL) });
        setFiles([]);
        setFilasSalvas([]);
        return;
      }
      try {
        const { data } = await api.get(`/prompt/${promptId}`);
        const filas = Array.isArray(data.queues) ? data.queues : [];
        // Espalhar sobre INITIAL evita campo `undefined` virando input não
        // controlado quando o registro é antigo e não tem a coluna nova.
        // Agente legado ("Conexão pendente") chega sem whatsappId: o select
        // fica vazio e as filas dele sobrevivem se forem da conexão escolhida.
        setPrompt({
          ...INITIAL,
          ...data,
          whatsappId: data.whatsappId || "",
          queueIds: filas.map((q) => Number(q.id)),
          atendeAntesDaFila: !!data.atendeAntesDaFila,
        });
        setFilasSalvas(filas);
        setFiles(data.files || []);
      } catch (err) {
        toastError(err);
      }
    }
    loadPrompt();
  }, [promptId, open]);

  // O status do arquivo muda no servidor (indexação); sem o socket a linha
  // ficaria "Processando" para sempre até reabrir o modal.
  useEffect(() => {
    if (!socket || !user?.companyId || !promptId) return undefined;
    const canal = `company-${user.companyId}-promptfile`;
    const onEvent = (data) => {
      if (data.action === "update" && data.promptFile?.promptId === promptId) {
        setFiles((prev) => {
          const idx = prev.findIndex((f) => f.id === data.promptFile.id);
          if (idx === -1) return [...prev, data.promptFile];
          const novo = [...prev];
          novo[idx] = data.promptFile;
          return novo;
        });
      }
      if (data.action === "delete") {
        setFiles((prev) => prev.filter((f) => f.id !== data.fileId));
      }
    };
    socket.on(canal, onEvent);
    return () => socket.off(canal, onEvent);
  }, [socket, user, promptId]);

  // Pré-visualização do prompt final: o servidor monta a partir do rascunho,
  // então a ordem dos blocos é sempre a mesma que vai para a IA.
  const camposDoPreview = JSON.stringify({
    agentName: prompt.agentName,
    tone: prompt.tone,
    useEmojis: prompt.useEmojis,
    restrictions: prompt.restrictions,
    transferInstructions: prompt.transferInstructions,
    prompt: prompt.prompt,
    knowledgeText: prompt.knowledgeText,
  });

  useEffect(() => {
    if (!open || tab !== 2) return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/prompt/preview", JSON.parse(camposDoPreview));
        setPreview(data);
      } catch (err) {
        setPreview(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [camposDoPreview, open, tab]);

  const handleClose = useCallback(() => {
    setPrompt(INITIAL);
    setFiles([]);
    setFilasSalvas([]);
    setTab(0);
    setPreview(null);
    setShowApiKey(false);
    onClose();
  }, [onClose]);

  const conexoes = useMemo(
    () => completaFilasDasConexoes(whatsApps, queues),
    [whatsApps, queues]
  );
  const conexaoAtual = conexoes.find(
    (w) => prompt.whatsappId !== "" && Number(w.id) === Number(prompt.whatsappId)
  );
  const filasConexao = filasDaConexao(conexaoAtual);
  const ocupacao = ocupacaoDasFilas({
    prompts: agentes,
    whatsappId: prompt.whatsappId,
    promptIdAtual: promptId,
  });
  const antesOcupadoPor = antesDaFilaOcupadoPor({
    whatsapps: conexoes,
    prompts: agentes,
    whatsappId: prompt.whatsappId,
    promptIdAtual: promptId,
  });
  // Fila marcada que saiu da conexão (vínculo órfão) só existe com conexão
  // escolhida; sem conexão, o legado ainda não tem contra o que comparar.
  const idsFora = conexaoAtual ? filasForaDaConexao(prompt.queueIds, conexaoAtual) : [];
  const queueIdsValidos = limpaFilasInvalidas(prompt.queueIds, conexaoAtual);
  const antesDaFila = !!prompt.atendeAntesDaFila && !antesOcupadoPor;
  const nomeDaFila = (id) =>
    filasConexao.find((q) => Number(q.id) === Number(id))?.name ||
    filasSalvas.find((q) => Number(q.id) === Number(id))?.name ||
    queues.find((q) => Number(q.id) === Number(id))?.name ||
    String(id);

  const avisos = [];
  if (promptId && prompt.id && !prompt.whatsappId) {
    avisos.push(i18n.t("aeros.talkai.modal.pendingWarning"));
  }
  if (idsFora.length) {
    avisos.push(
      i18n.t("aeros.talkai.modal.warnOutsideQueues", {
        queues: idsFora.map(nomeDaFila).join(", "),
      })
    );
  }
  const comIntegracao = filasComIntegracao(queueIdsValidos, conexaoAtual);
  if (comIntegracao.length) {
    avisos.push(
      i18n.t("aeros.talkai.modal.warnQueueIntegration", {
        queues: comIntegracao.map((q) => q.name).join(", "),
      })
    );
  }
  const comChatbot = filasComChatbot(queueIdsValidos, conexaoAtual);
  if (comChatbot.length) {
    avisos.push(
      i18n.t("aeros.talkai.modal.warnQueueChatbot", {
        queues: comChatbot.map((q) => q.name).join(", "),
      })
    );
  }
  if (conexaoTemIntegracao(conexaoAtual)) {
    avisos.push(i18n.t("aeros.talkai.modal.warnConnectionIntegration"));
  }
  if (antesDaFila && conexaoTemMenu(conexaoAtual)) {
    avisos.push(i18n.t("aeros.talkai.modal.preQueueNoEffect"));
  }
  const extras = conexoesComAntesDaFilaExtra({
    whatsapps: conexoes,
    promptId,
    whatsappId: prompt.whatsappId,
  });
  if (extras.length) {
    avisos.push(
      i18n.t("aeros.talkai.modal.warnExtraConnections", {
        connections: extras.map((w) => w.name).join(", "),
      })
    );
  }

  const trocaConexao = (valor) => {
    const nova = conexoes.find((w) => Number(w.id) === Number(valor));
    setPrompt((atual) => ({
      ...atual,
      whatsappId: valor,
      queueIds: limpaFilasInvalidas(atual.queueIds, nova),
    }));
  };

  const handleSave = async () => {
    const vinculo = {
      whatsappId: prompt.whatsappId === "" ? null : Number(prompt.whatsappId),
      queueIds: queueIdsValidos,
      atendeAntesDaFila: antesDaFila,
    };
    const erro = erroDoVinculo(vinculo);
    if (erro) {
      toast.error(i18n.t(`backendErrors.${erro}`));
      return;
    }
    // `queueId` é legado e o backend o ignora; `queues`/`whatsapp`/`pendente`
    // são campos de leitura da API.
    const {
      queueId: _legado,
      queues: _filas,
      whatsapp: _conexao,
      pendente: _pendente,
      ...dados
    } = prompt;
    const payload = { ...dados, ...vinculo };
    setSaving(true);
    try {
      if (promptId) await api.put(`/prompt/${promptId}`, payload);
      else await api.post("/prompt", payload);
      toast.success(i18n.t("aeros.talkai.modal.saved"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const arquivo = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!arquivo || !promptId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", arquivo);
      const { data } = await api.post(`/prompt/${promptId}/files`, formData);
      setFiles((prev) => {
        const idx = prev.findIndex((f) => f.id === data.id);
        if (idx === -1) return [...prev, data];
        const novo = [...prev];
        novo[idx] = data;
        return novo;
      });
      toast.success(i18n.t("aeros.talkai.files.uploaded"));
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await api.delete(`/prompt/files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success(i18n.t("aeros.talkai.files.deleted"));
    } catch (err) {
      toastError(err);
    }
  };

  const resumo = filesSummary(files);
  // A simulação cita a primeira fila escolhida como destino da transferência.
  const queueAtual = queueIdsValidos.length
    ? { name: nomeDaFila(queueIdsValidos[0]) }
    : null;

  const renderLabel = (texto, obrigatorio, id) => (
    <div className={classes.label} id={id}>
      {texto}
      {obrigatorio && <span className={classes.required}>*</span>}
    </div>
  );

  const TABS = [
    { icon: FaceIcon, label: i18n.t("aeros.talkai.tabs.personality") },
    { icon: MenuBookIcon, label: i18n.t("aeros.talkai.tabs.knowledge") },
    { icon: TuneIcon, label: i18n.t("aeros.talkai.tabs.advanced") },
  ];

  // ---------- aba 1: Personalidade ----------
  const renderPersonality = () => {
    const roteiro = simulationScript({
      agentName: prompt.agentName,
      tone: prompt.tone,
      useEmojis: prompt.useEmojis,
      transferMessage: prompt.transferMessage,
      transferInstructions: prompt.transferInstructions,
      queueName: queueAtual?.name,
    });
    return (
      <div className={`${classes.split} ${classes.splitPersonality}`}>
        <div className={classes.left}>
          <div className={classes.pair}>
            <div>
              {renderLabel(i18n.t("aeros.talkai.form.agentName"))}
              <TextField
                value={prompt.agentName || ""}
                onChange={(e) => change("agentName", e.target.value)}
                variant="outlined"
                fullWidth
                className={classes.input}
                inputProps={{ "aria-label": i18n.t("aeros.talkai.form.agentName") }}
              />
            </div>
            <div>
              {renderLabel(i18n.t("aeros.talkai.form.tone"))}
              <div className={classes.segmented} role="radiogroup">
                {TONES.map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    role="radio"
                    aria-checked={prompt.tone === tone}
                    className={`${classes.segment} ${
                      prompt.tone === tone ? classes.segmentActive : ""
                    }`}
                    onClick={() => change("tone", tone)}
                  >
                    {toneLabel(tone)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={classes.optionRow}>
            <MoodIcon />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.optionTitle}>
                {i18n.t("aeros.talkai.form.useEmojis")}
              </div>
            </div>
            <Switch
              classes={{ root: classes.switchRoot }}
              checked={!!prompt.useEmojis}
              onChange={(e) => change("useEmojis", e.target.checked)}
              inputProps={{ "aria-label": i18n.t("aeros.talkai.form.useEmojis") }}
            />
          </div>
          <div className={classes.hint}>{i18n.t("aeros.talkai.form.personalityHint")}</div>

          <div>
            {renderLabel(i18n.t("aeros.talkai.form.restrictions"))}
            <TextField
              value={prompt.restrictions || ""}
              onChange={(e) => change("restrictions", e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              className={`${classes.input} ${classes.inputMultiline}`}
              inputProps={{ "aria-label": i18n.t("aeros.talkai.form.restrictions") }}
            />
            <div className={classes.hint}>
              {i18n.t("aeros.talkai.form.restrictionsHelper")}
            </div>
          </div>

          <div>
            {renderLabel(i18n.t("aeros.talkai.form.transferInstructions"))}
            <TextField
              value={prompt.transferInstructions || ""}
              onChange={(e) => change("transferInstructions", e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              className={`${classes.input} ${classes.inputMultiline}`}
              inputProps={{
                "aria-label": i18n.t("aeros.talkai.form.transferInstructions"),
              }}
            />
            <div className={classes.hint}>
              {i18n.t("aeros.talkai.form.transferInstructionsHelper")}
            </div>
          </div>

          <div>
            {renderLabel(i18n.t("aeros.talkai.form.transferMessage"))}
            <TextField
              value={prompt.transferMessage || ""}
              onChange={(e) => change("transferMessage", e.target.value)}
              variant="outlined"
              fullWidth
              className={classes.input}
              inputProps={{
                "aria-label": i18n.t("aeros.talkai.form.transferMessage"),
              }}
            />
          </div>
        </div>

        <div className={classes.right}>
          <div className={classes.sideTitle}>
            {i18n.t("aeros.talkai.simulation.title")}
          </div>
          <div className={classes.chat}>
            {roteiro.messages.map((msg, idx) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                className={
                  msg.from === "agent" ? classes.bubbleAgent : classes.bubbleContact
                }
              >
                {msg.text}
              </div>
            ))}
            <div className={classes.transferTag}>
              <SwapHorizIcon />
              {roteiro.transferReason}
            </div>
          </div>
          <div className={classes.sideNote}>
            <InfoOutlinedIcon />
            {i18n.t("aeros.talkai.simulation.note")}
          </div>
        </div>
      </div>
    );
  };

  // ---------- aba 2: Conhecimento ----------
  const renderKnowledge = () => (
    <div className={`${classes.split} ${classes.splitKnowledge}`}>
      <div className={classes.left}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className={classes.label} style={{ marginBottom: 0 }}>
            {i18n.t("aeros.talkai.form.knowledgeText")}
          </span>
          <span className={classes.counter}>
            {knowledgeCounter(prompt.knowledgeText)}
          </span>
        </div>
        <TextField
          value={prompt.knowledgeText || ""}
          onChange={(e) => change("knowledgeText", e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          minRows={12}
          className={`${classes.input} ${classes.inputMultiline}`}
          inputProps={{
            maxLength: KNOWLEDGE_LIMIT,
            "aria-label": i18n.t("aeros.talkai.form.knowledgeText"),
          }}
        />
        <div className={classes.hint}>
          {i18n.t("aeros.talkai.form.knowledgeHelper")}
        </div>
      </div>

      <div className={classes.right}>
        <div className={classes.sideTitleRow}>
          <span className={classes.label} style={{ marginBottom: 0 }}>
            {i18n.t("aeros.talkai.files.title")}
            <span className={classes.counter} style={{ marginLeft: 4 }}>
              {i18n.t("aeros.talkai.files.counter", {
                count: files.length,
                max: MAX_FILES,
              })}
            </span>
          </span>
          <Button
            className={classes.importButton}
            startIcon={uploading ? undefined : <CloudUploadIcon />}
            disabled={!promptId || uploading || files.length >= MAX_FILES}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <CircularProgress size={14} />
            ) : (
              i18n.t("aeros.talkai.files.import")
            )}
          </Button>
        </div>

        <Tooltip
          title={promptId ? "" : i18n.t("aeros.talkai.files.saveFirst")}
          placement="top"
        >
          <button
            type="button"
            className={`${classes.dropzone} ${
              promptId ? "" : classes.dropzoneDisabled
            }`}
            onClick={() => promptId && fileInputRef.current?.click()}
            disabled={!promptId}
          >
            <CloudUploadIcon />
            <div className={classes.dropTitle}>
              {i18n.t("aeros.talkai.files.dropTitle")}
            </div>
            <div className={classes.dropHint}>
              {promptId
                ? i18n.t("aeros.talkai.files.dropHint")
                : i18n.t("aeros.talkai.files.saveFirst")}
            </div>
          </button>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".pdf,.txt,.docx"
          onChange={handleUpload}
        />

        <div className={classes.fileList} style={{ marginTop: 12 }}>
          {files.map((arquivo) => {
            const { Icon, color } = fileVisual(arquivo.name);
            const estado = arquivo.status || "processing";
            const tom =
              estado === "ready"
                ? AEROS.success
                : estado === "failed"
                ? AEROS.error
                : AEROS.warning;
            const EstadoIcon =
              estado === "ready"
                ? CheckCircleIcon
                : estado === "failed"
                ? ErrorIcon
                : HourglassEmptyIcon;
            return (
              <div key={arquivo.id} className={classes.fileRow}>
                <span
                  className={classes.fileTile}
                  style={{ backgroundColor: alpha(color, 0.14), color }}
                >
                  <Icon />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={classes.fileName} title={arquivo.name}>
                    {arquivo.name}
                  </div>
                  <div className={classes.fileMeta}>
                    {formatFileSize(arquivo.size)}
                  </div>
                </div>
                <span
                  className={classes.filePill}
                  style={{
                    color: stateText(theme, tom, ""),
                    backgroundColor: alpha(tom, 0.12),
                  }}
                >
                  <EstadoIcon />
                  {i18n.t(`aeros.talkai.files.${estado}`)}
                </span>
                <Tooltip title={i18n.t("aeros.talkai.files.delete")}>
                  <IconButton
                    className={classes.fileDelete}
                    onClick={() => handleDeleteFile(arquivo.id)}
                    aria-label={i18n.t("aeros.talkai.files.delete")}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </div>
            );
          })}
        </div>

        {resumo.firstFailure && (
          <div className={classes.failureLine}>
            <ErrorOutlineIcon />
            <span>
              {i18n.t("aeros.talkai.files.failureLine", {
                name: resumo.firstFailure.name,
                reason:
                  resumo.firstFailure.failureReason ||
                  i18n.t("aeros.talkai.files.failureUnknown"),
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // ---------- aba 3: Avançado ----------
  const renderAdvanced = () => (
    <div className={`${classes.split} ${classes.splitAdvanced}`}>
      <div className={classes.left}>
        <div>
          {renderLabel(i18n.t("aeros.talkai.form.apiKey"), true)}
          <TextField
            value={prompt.apiKey || ""}
            onChange={(e) => change("apiKey", e.target.value)}
            type={showApiKey ? "text" : "password"}
            variant="outlined"
            fullWidth
            className={`${classes.input} ${classes.inputMono}`}
            inputProps={{ "aria-label": i18n.t("aeros.talkai.form.apiKey") }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowApiKey((v) => !v)}
                    aria-label={i18n.t(
                      showApiKey
                        ? "aeros.talkai.form.hideKey"
                        : "aeros.talkai.form.showKey"
                    )}
                  >
                    {showApiKey ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={classes.pair}>
          <div>
            {renderLabel(i18n.t("aeros.talkai.form.model"))}
            <TextField
              select
              value={prompt.model || MODELS[0]}
              onChange={(e) => change("model", e.target.value)}
              variant="outlined"
              fullWidth
              className={`${classes.input} ${classes.inputMono}`}
            >
              {MODELS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div>
            {renderLabel(i18n.t("aeros.talkai.form.response"))}
            <TextField
              select
              value={prompt.voice || "texto"}
              onChange={(e) => change("voice", e.target.value)}
              variant="outlined"
              fullWidth
              className={classes.input}
              SelectProps={{
                renderValue: (value) =>
                  value === "texto" ? (
                    i18n.t("aeros.talkai.form.responseText")
                  ) : (
                    <span className={classes.queueOption}>
                      <GraphicEqIcon
                        style={{
                          fontSize: 16,
                          color: stateText(theme, "#C08BFF", "purple"),
                        }}
                      />
                      {`${i18n.t("aeros.talkai.form.voicePrefix")} · ${voiceShortName(
                        value
                      )}`}
                    </span>
                  ),
              }}
            >
              <MenuItem value="texto">
                {i18n.t("aeros.talkai.form.responseText")}
              </MenuItem>
              {VOICES.map((v) => (
                <MenuItem key={v} value={v}>
                  {voiceShortName(v)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>

        <div className={classes.azureBlock}>
          <div className={classes.azureTitle}>
            {i18n.t("aeros.talkai.form.azureTitle")}
          </div>
          <div>
            {renderLabel(i18n.t("aeros.talkai.form.azureKey"))}
            <TextField
              value={prompt.voiceKey || ""}
              onChange={(e) => change("voiceKey", e.target.value)}
              type="password"
              variant="outlined"
              fullWidth
              className={`${classes.input} ${classes.inputMono}`}
              inputProps={{ "aria-label": i18n.t("aeros.talkai.form.azureKey") }}
            />
          </div>
          <div>
            {renderLabel(i18n.t("aeros.talkai.form.azureRegion"))}
            <TextField
              value={prompt.voiceRegion || ""}
              onChange={(e) => change("voiceRegion", e.target.value)}
              variant="outlined"
              fullWidth
              className={`${classes.input} ${classes.inputMono}`}
              placeholder="brazilsouth"
              inputProps={{ "aria-label": i18n.t("aeros.talkai.form.azureRegion") }}
            />
          </div>
        </div>

        <div className={classes.trio}>
          {[
            {
              campo: "temperature",
              label: i18n.t("aeros.talkai.form.temperature"),
              hint: i18n.t("aeros.talkai.form.temperatureHint"),
              step: 0.1,
            },
            {
              campo: "maxTokens",
              label: i18n.t("aeros.talkai.form.maxTokens"),
              hint: i18n.t("aeros.talkai.form.maxTokensHint"),
              step: 10,
            },
            {
              campo: "maxMessages",
              label: i18n.t("aeros.talkai.form.maxMessages"),
              hint: i18n.t("aeros.talkai.form.maxMessagesHint"),
              step: 1,
            },
          ].map(({ campo, label, hint, step }) => (
            <div key={campo}>
              {renderLabel(label)}
              <TextField
                type="number"
                value={prompt[campo]}
                onChange={(e) => change(campo, e.target.value)}
                variant="outlined"
                fullWidth
                className={`${classes.input} ${classes.numeric}`}
                inputProps={{ step, "aria-label": label }}
              />
              <div className={classes.hintSmall}>{hint}</div>
            </div>
          ))}
        </div>

        <div>
          {renderLabel(i18n.t("aeros.talkai.form.basePrompt"), true)}
          <TextField
            value={prompt.prompt || ""}
            onChange={(e) => change("prompt", e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            className={`${classes.input} ${classes.inputMultiline}`}
            inputProps={{
              "aria-label": i18n.t("aeros.talkai.form.basePrompt"),
              "aria-describedby": "prompt-base-ajuda",
            }}
          />
          <div id="prompt-base-ajuda" className={classes.hint}>
            {i18n.t("aeros.talkai.form.basePromptHelper")}
          </div>
        </div>
      </div>

      <div className={classes.right}>
        <div className={classes.sideTitleRow}>
          <span className={classes.sideTitle} style={{ marginBottom: 0 }}>
            {i18n.t("aeros.talkai.preview.title")}
          </span>
          <Button
            className={classes.copyButton}
            startIcon={<FileCopyOutlinedIcon />}
            disabled={!preview?.text}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(preview.text);
                toast.success(i18n.t("aeros.talkai.preview.copied"));
              } catch (err) {
                toastError(err);
              }
            }}
          >
            {i18n.t("aeros.talkai.preview.copy")}
          </Button>
        </div>

        <div className={classes.blocks}>
          {preview?.blocks?.length ? (
            preview.blocks.map((bloco) => {
              const cor =
                {
                  basePrompt: AEROS.cyan,
                  restrictions: "#C08BFF",
                  transfer: AEROS.warning,
                  knowledge: AEROS.success,
                }[bloco.key] || theme.palette.text.secondary;
              return (
                <div
                  key={bloco.key}
                  className={classes.block}
                  style={{ borderLeftColor: cor }}
                >
                  <div className={classes.blockTitle} style={{ color: cor }}>
                    {i18n.t(`aeros.talkai.blocks.${bloco.key}`)}
                  </div>
                  <pre className={classes.blockText}>{bloco.text}</pre>
                </div>
              );
            })
          ) : (
            <div className={classes.dropHint}>
              {i18n.t("aeros.talkai.preview.error")}
            </div>
          )}
        </div>

        <div className={classes.sideNote}>
          <InfoOutlinedIcon />
          {i18n.t("aeros.talkai.preview.note")}
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className={classes.footer}>
      {tab === 1 && resumo.ready > 0 ? (
        <span className={classes.footerReady}>
          <CheckCircleIcon />
          {i18n.t(
            resumo.ready === 1
              ? "aeros.talkai.files.readyCountOne"
              : "aeros.talkai.files.readyCount",
            { count: resumo.ready }
          )}
        </span>
      ) : tab === 2 && preview?.approxTokens ? (
        <span className={classes.footerNote}>
          {i18n.t("aeros.talkai.preview.tokens", { count: preview.approxTokens })}
        </span>
      ) : (
        <span className={classes.footerNote}>
          {i18n.t("aeros.talkai.modal.footerNote")}
        </span>
      )}
      <div className={classes.footerActions}>
        <Button className={classes.cancelButton} onClick={handleClose} disabled={saving}>
          {i18n.t("aeros.flow.cancel")}
        </Button>
        <Button
          className={classes.saveButton}
          startIcon={saving ? undefined : <AllInclusiveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={18} />
          ) : (
            i18n.t("aeros.talkai.modal.save")
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={900}
      title={i18n.t(promptId ? "aeros.talkai.modal.edit" : "aeros.talkai.modal.add")}
      subtitle={
        promptId && prompt.name
          ? [prompt.name, conexaoAtual?.name].filter(Boolean).join(" — ")
          : i18n.t("aeros.talkai.modal.subtitle")
      }
      footer={footer}
      flushBody
      loading={saving}
    >
      <div className={classes.headFields}>
        <div>
          {renderLabel(i18n.t("aeros.talkai.modal.name"), true)}
          <TextField
            value={prompt.name || ""}
            onChange={(e) => change("name", e.target.value)}
            variant="outlined"
            fullWidth
            className={classes.input}
            inputProps={{ "aria-label": i18n.t("aeros.talkai.modal.name") }}
          />
        </div>
        <div>
          {renderLabel(i18n.t("aeros.talkai.modal.connection"), true, "prompt-connection-label")}
          <TextField
            select
            value={prompt.whatsappId === "" ? "" : Number(prompt.whatsappId)}
            onChange={(e) => trocaConexao(e.target.value)}
            variant="outlined"
            fullWidth
            required
            className={classes.input}
            SelectProps={{
              displayEmpty: true,
              labelId: "prompt-connection-label",
              renderValue: (value) => {
                const conexao = conexoes.find((w) => Number(w.id) === Number(value));
                if (!conexao) {
                  return (
                    <span className={classes.placeholder}>
                      {i18n.t("aeros.talkai.modal.selectConnection")}
                    </span>
                  );
                }
                return conexao.name;
              },
            }}
          >
            {conexoes.map((conexao) => (
              <MenuItem
                key={conexao.id}
                value={Number(conexao.id)}
                className={classes.menuItem}
              >
                {conexao.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div className={classes.vinculoFields}>
        <div>
          {renderLabel(i18n.t("aeros.talkai.modal.queues"), false, "prompt-queues-label")}
          <TextField
            select
            value={prompt.queueIds}
            onChange={(e) =>
              change(
                "queueIds",
                (Array.isArray(e.target.value) ? e.target.value : []).map(Number)
              )
            }
            variant="outlined"
            fullWidth
            disabled={!conexaoAtual || (filasConexao.length === 0 && idsFora.length === 0)}
            className={classes.input}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              labelId: "prompt-queues-label",
              renderValue: (selecionadas) => {
                if (!conexaoAtual) {
                  return (
                    <span className={classes.placeholder}>
                      {i18n.t("aeros.talkai.modal.selectConnectionFirst")}
                    </span>
                  );
                }
                if (!selecionadas.length) {
                  return (
                    <span className={classes.placeholder}>
                      {filasConexao.length
                        ? i18n.t("aeros.talkai.modal.selectQueues")
                        : i18n.t("aeros.talkai.modal.noQueuesInConnection")}
                    </span>
                  );
                }
                return (
                  <span className={classes.queueChips}>
                    {selecionadas.map((id) => {
                      const fila = filasConexao.find((q) => Number(q.id) === Number(id));
                      return (
                        <span
                          key={id}
                          className={`${classes.queueChip} ${
                            fila ? "" : classes.queueChipOutside
                          }`}
                        >
                          <span
                            className={classes.queueDot}
                            style={{ backgroundColor: fila?.color || AEROS.cyan }}
                          />
                          {nomeDaFila(id)}
                        </span>
                      );
                    })}
                  </span>
                );
              },
            }}
          >
            {filasConexao.map((fila) => {
              const marcada = prompt.queueIds.includes(Number(fila.id));
              const dono = ocupacao[Number(fila.id)];
              return (
                <MenuItem
                  key={fila.id}
                  value={Number(fila.id)}
                  disabled={!!dono && !marcada}
                  className={classes.menuItem}
                >
                  <Checkbox
                    checked={marcada}
                    size="small"
                    className={classes.menuCheck}
                    inputProps={{ "aria-hidden": true, tabIndex: -1 }}
                  />
                  <span
                    className={classes.queueDot}
                    style={{ backgroundColor: fila.color || AEROS.cyan }}
                  />
                  <span className={classes.menuText}>
                    {fila.name}
                    {dono && (
                      <span className={classes.menuSecondary}>
                        {i18n.t("aeros.talkai.modal.queueUsedBy", { name: dono })}
                      </span>
                    )}
                  </span>
                </MenuItem>
              );
            })}
            {idsFora.map((id) => (
              <MenuItem key={`fora-${id}`} value={id} className={classes.menuItem}>
                <Checkbox
                  checked={prompt.queueIds.includes(id)}
                  size="small"
                  className={classes.menuCheck}
                  inputProps={{ "aria-hidden": true, tabIndex: -1 }}
                />
                <span className={classes.menuText}>
                  {nomeDaFila(id)}
                  <span className={classes.menuSecondary}>
                    {i18n.t("aeros.talkai.modal.queueOutside")}
                  </span>
                </span>
              </MenuItem>
            ))}
          </TextField>
        </div>

        <label
          className={`${classes.optionRow} ${classes.preQueueRow} ${
            antesOcupadoPor || !conexaoAtual ? classes.preQueueDisabled : ""
          }`}
        >
          <Checkbox
            checked={antesDaFila}
            disabled={!!antesOcupadoPor || !conexaoAtual}
            onChange={(e) => change("atendeAntesDaFila", e.target.checked)}
            className={classes.preQueueCheck}
            inputProps={{ "aria-describedby": "prompt-prequeue-help" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.optionTitle}>
              {i18n.t("aeros.talkai.modal.preQueue")}
            </div>
            <div className={classes.hint} id="prompt-prequeue-help">
              {antesOcupadoPor
                ? i18n.t("aeros.talkai.modal.preQueueTaken", { name: antesOcupadoPor })
                : i18n.t("aeros.talkai.modal.preQueueHelp")}
            </div>
          </div>
        </label>

        {avisos.length > 0 && (
          <div
            className={classes.warnings}
            role="status"
            aria-live="polite"
            aria-label={i18n.t("aeros.talkai.modal.warningsLabel")}
          >
            {avisos.map((texto) => (
              <div key={texto} className={classes.warningLine}>
                <ReportProblemOutlinedIcon />
                <span>{texto}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={classes.tabsRow} role="tablist">
        {TABS.map((item, idx) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              type="button"
              role="tab"
              aria-selected={tab === idx}
              className={`${classes.tab} ${tab === idx ? classes.tabActive : ""}`}
              onClick={() => setTab(idx)}
            >
              <Icon />
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === 0 && renderPersonality()}
      {tab === 1 && renderKnowledge()}
      {tab === 2 && renderAdvanced()}
    </AerosDialog>
  );
};

export default PromptModal;
